import {
  BANK_BORDER,
  BANK_BORDER_SOFT,
  BANK_FIGURE,
  BANK_FS_BODY,
  BANK_FS_LEAD,
  BANK_FS_META,
  BANK_FS_READ,
  BANK_OK,
  BANK_TEXT,
  BANK_TEXT_2,
} from "@hakili/ui";
import { formatDateAndTime } from "@hakili/i18n";
import { Lock } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

import { DASH } from "./dossiers.js";

/**
 * D2's `.card` and `.chip` as the bank screens built to the drawing share them (DESIGN SPRINT D-1026-2, Lane E): the card with its
 * head (title 16/600, sub 14 px in `--hk-text-2`, the chip at the right) and `.chip` (13 px, 600, a pill; `ok` is the bank's green).
 */
export function D2Chip(props: {
  readonly children: ReactNode;
  readonly tone?: "neutral" | "ok" | "warn" | "brand";
  readonly testId?: string;
  /** A chip whose words are served and may be long: it wraps inside its container instead of running past it. */
  readonly wrap?: boolean;
}): ReactElement {
  const tone = props.tone ?? "neutral";
  const shape =
    props.wrap === true
      ? "max-w-full whitespace-normal rounded-2xl py-[5px] leading-snug"
      : "whitespace-nowrap rounded-full py-[5px] leading-none";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 ${shape} ${BANK_FS_META} font-semibold ${
        tone === "ok"
          ? BANK_OK
          : tone === "warn"
            ? "bg-[color:var(--hk-warn-bg)] text-[color:var(--hk-warn)]"
            : tone === "brand"
              ? "bg-[color:var(--hk-brand-tint)] text-[color:var(--hk-brand-strong)]"
              : `bg-[color:var(--hk-neutral-bg)] ${BANK_TEXT}`
      }`}
      data-testid={props.testId}
    >
      {props.children}
    </span>
  );
}

export function D2Card(props: {
  readonly title: string;
  readonly testId?: string;
  readonly sub?: ReactNode;
  readonly subTestId?: string;
  readonly right?: ReactNode;
  readonly flush?: boolean;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section
      className={`mb-4 overflow-hidden rounded-[var(--hk-r-lg)] border bg-[color:var(--hk-surface)] ${BANK_BORDER}`}
      data-testid={props.testId}
    >
      <div className="flex items-center gap-3 px-5 pb-1 pt-4">
        <h3 className={`${BANK_FS_LEAD} font-semibold leading-[1.3] ${BANK_TEXT}`}>{props.title}</h3>
        {props.sub === undefined ? null : (
          <small className={`${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid={props.subTestId}>
            {props.sub}
          </small>
        )}
        {props.right === undefined ? null : <span className="ml-auto">{props.right}</span>}
      </div>
      <div className={props.flush === true ? "pb-0 pt-3" : "px-5 pb-[18px] pt-3"}>{props.children}</div>
    </section>
  );
}

/** D2 `.guard`: a guarded button's reason is a chip with the lock (lane C's `reasonDress` / `reasonIcon`, bound once here). */
export const GUARD_CHIP = {
  reasonDress: "chip",
  reasonIcon: <Lock className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />,
} as const;

/** The guarded foot button's wrapper (the reason sits beside it): D2's large height and size, and no padding of its own, so the button stands at the bar's padding edge. */
export const GUARD_FOOT = "min-h-[48px] text-[length:var(--hk-fs-read)]";

/** D2 `.kv` row: the label in the muted ink, the value right, 600; the figure face unless `plain` (« Durée » is the page's own). */
export function D2Kv(props: {
  readonly label: string;
  readonly value: string;
  /** D2 draws « Durée » in the page's own face, every other figure in the figure face. */
  readonly plain?: boolean;
  readonly testId?: string;
}): ReactElement {
  return (
    <div
      className={`flex justify-between gap-4 border-b py-[9px] last:border-b-0 ${BANK_BORDER_SOFT}`}
      data-testid={props.testId}
    >
      <dt className={`${BANK_FS_READ} ${BANK_TEXT_2}`}>{props.label}</dt>
      <dd className={`text-right font-semibold ${props.plain === true ? BANK_FS_READ : BANK_FIGURE}`}>
        {props.value}
      </dd>
    </div>
  );
}

/** « 20 octobre, 08:41 »: D2's chain date (no year, a time). */
export const dayMonthTime = (value: string | null): string => {
  if (value === null) return DASH;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return DASH;
  return formatDateAndTime(instant).replace(/[\s\u00a0\u202f]\d{4}(?=,)/u, "");
};
